import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Link, Center, HStack, Alert } from '@chakra-ui/react';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import authenticationService from '../../../../../service/authenticationService';
import { getInputFieldProps as _getInputFieldProps } from '../../../utils/propsBuilder';
import extractServerErrorMessages from '../../../utils/extractServerErrorMessages';
import loggerService from '../../../../../utils/loggerService';
import { PANEL } from '../../../constants/panel';
import { useAppContext } from '../../../context/appContext';
import { useFlash, useRecaptcha } from '../../../hooks';
import {
  Button,
  Input,
  TitleText,
  ErrorBanner,
  RecaptchaCheckbox,
} from '../../../components';
import {
  FacebookAccount,
  LineAccount,
  OAuthApplicationLinks,
  SocialAccountDivider,
} from '../../components';
import { QUOTA_LIMIT_ERROR_TYPE } from '../../../../../shop/constants/quota_limit';
import pnotifyService from '../../../../../service/pnotifyService';
import {
  EVENT_CATEGORY_FOR_USER_FLOW,
  VALUE_FOR_USER_FLOW,
  PROPERTY_FOR_USER_FLOW,
  EVENT_NAME_FOR_USER_FLOW,
} from '../../../../../shop/constants/signupLoginEventsTracking';
import regex from '../../../../../shop/constants/regex';
import trackerService from '../../../../../service/trackerService';
import GoogleAccountButton from '../../components/GoogleAccountButton';
import { getThirdPartyOptions } from '../../../utils/getThirdPartyOptions';
import { delayRedirect } from '../../../../../utils/redirect';
import { hasFeature } from '../../../../../utils/featureService';
import {
  AUTH_SUCCESS_TOAST_TYPE,
  reloadWithAuthSuccessToast,
  withAuthSuccessToastQuery,
} from '../../../../../utils/authSuccessToastQuery';
import MembershipPromoBannerText from '../../../components/MembershipPromoBanner/MembershipPromoBannerText';
import MembershipPromoBannerImage from '../../../components/MembershipPromoBanner/MembershipPromoBannerImage';
import { SIGNUP_LOGIN_E2E_TEST_IDS } from '../../../constants/e2eTestId';

const RESET_PASSWORD_PATH = '/users/password/new';
const COMPLETE_INFO_PATH = '/users/complete_info';

export default function LoginForm() {
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const {
    setCurrentPanel,
    defaultAccount,
    isRecaptchaEnabled,
    isFacebookLoginEnabled,
    isLineLoginEnabled,
    isGoogleSignUpLoginEnabled,
    isSocialLoginEnabled,
    customerAuthenticationOauthApplications,
    userSetting,
    defaultMembershipPromoBanner,
    slFeatureService,
    isVariantLoading,
    isVariantUI,
    hasThirdpartyLoginUIRevamp,
    isOnePageStorePopup,
    setMissingFields,
    setCurrentUser,
  } = useAppContext();
  const {
    errors,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext();
  const [errorMessages, setErrorMessages] = useState([]);
  const [showResetPasswordHint, setShowResetPasswordHint] = useState(false);
  const {
    membership_promo_banner: { text, image },
  } = userSetting;
  const hasUserCredits = slFeatureService.getFeatureLimit('user_credit', {})[
    'manual'
  ];
  const hasMembershipTiers = hasFeature('membership_tiers');
  const hasMemberPoints = hasFeature('member_points');
  const hasMemberJoinNotice = hasFeature('member_join_notice');
  const hasMemberGift = hasFeature('member_gift');

  const defaultTextConditions = useMemo(
    () => ({
      hasMemberGift,
      hasUserCredits,
      hasMembershipTiers,
      hasMemberPoints,
      defaultMembershipPromoBanner,
    }),
    [
      hasMemberGift,
      hasUserCredits,
      hasMembershipTiers,
      hasMemberPoints,
      defaultMembershipPromoBanner,
    ],
  );

  const i18nKey = {
    label: 'Login - email or mobile phone - label',
    placeholder: 'Login - email or mobile phone - placeholder',
  };

  const { field: emailAndMobileField } = useController({
    name: 'emailAndMobile',
    control,
    defaultValue: defaultAccount?.email || defaultAccount?.mobile_phone || null,
  });
  const { field: passwordField } = useController({
    name: 'password',
    control,
    defaultValue: '',
  });

  const onSubmit = async (data) => {
    setErrorMessages([]);
    setShowResetPasswordHint(false);
    const { password, emailAndMobile } = data;

    const loginMethod = regex.EMAIL_REGEX.test(emailAndMobile)
      ? VALUE_FOR_USER_FLOW.email
      : VALUE_FOR_USER_FLOW.mobile;

    await trackerService.track({
      type: EVENT_NAME_FOR_USER_FLOW.MemberLoginFormSubmit,
      data: {
        event_category: EVENT_CATEGORY_FOR_USER_FLOW.MemberLogin,
        property: PROPERTY_FOR_USER_FLOW.login_option,
        value: loginMethod,
      },
    });

    try {
      const urlQueries = new URLSearchParams(window.location.search);
      const encryptData = urlQueries.get('encrypt_data');
      const payload = {
        user: {
          password,
          mobile_phone_or_email: emailAndMobile,
        },
        encrypt_data: encryptData,
      };
      if (isRecaptchaEnabled) {
        const token = await getRecaptchaToken();
        payload[RECAPTCHA_TOKEN_FIELD] = token;
        if (recaptchaCheckboxToken) {
          payload['is_use_checkbox'] = true;
        }
        refReCAPTCHA.current?.reset();
        if (isShowCheckboxRecaptcha) {
          setIsShowCheckboxRecaptcha(false);
        }
      }
      const result = await authenticationService.signIn(payload);

      await trackerService.track({
        type: EVENT_NAME_FOR_USER_FLOW.MemberLoginSucceed,
        data: {
          event_category: EVENT_CATEGORY_FOR_USER_FLOW.MemberLogin,
          property: PROPERTY_FOR_USER_FLOW.login_option,
          value: loginMethod,
        },
      });

      const { redirect_to, user_id } = result.data;
      const redirectToWithToast = withAuthSuccessToastQuery(
        redirect_to,
        AUTH_SUCCESS_TOAST_TYPE.LOGIN,
      );

      if (isOnePageStorePopup) {
        if (redirect_to === COMPLETE_INFO_PATH) {
          try {
            const missingFieldsResponse =
              await authenticationService.getMissingFields();
            const missingFields = missingFieldsResponse?.data?.fields || [];

            if (missingFields.length > 0) {
              setCurrentUser({ _id: user_id });
              setMissingFields(missingFields);
              setCurrentPanel(PANEL.COMPLETE_INFO);
              return;
            }
          } catch (error) {
            loggerService.error(error, {
              context: {
                debugMessage: 'unable to get missing fields after sign in.',
              },
            });
            reloadWithAuthSuccessToast(AUTH_SUCCESS_TOAST_TYPE.LOGIN);
            return;
          }
        }

        reloadWithAuthSuccessToast(AUTH_SUCCESS_TOAST_TYPE.LOGIN);
        return;
      }

      delayRedirect(redirectToWithToast);
    } catch (error) {
      const data = error?.response?.data ?? {};
      let input = data;
      if (
        data.error_code === QUOTA_LIMIT_ERROR_TYPE.TRIAL_EMAIL_QUOTA_EXCEEDED
      ) {
        return pnotifyService.notify(
          t('Verification - email - trial merchant - reach limit - failure'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
      } else if (isRecaptchaError({ errorCode: data?.error_code })) {
        refCheckboxReCAPTCHA.current?.reset();
        setIsShowCheckboxRecaptcha(true);
      } else {
        if (data.unconfirmed_email && !data.reached_rate_limit) {
          input = {
            error_messages: [
              t('Login - Error - unconfirmed email', {
                email: data.unconfirmed_email,
              }),
            ],
          };
        }
        setErrorMessages(extractServerErrorMessages(input));
      }
    }
  };

  const getInputFieldProps = useCallback(
    ({ field, error }) => _getInputFieldProps({ field, error, t }),
    [t],
  );

  const handleResetPasswordClick = () => {
    setErrorMessages([]);
    setShowResetPasswordHint(true);
  };

  useFlash((flash) => {
    if (typeof flash?.alert === 'string' && flash.alert) {
      setErrorMessages(
        extractServerErrorMessages({
          error_messages: [flash.alert],
        }),
      );
    }
  });

  useEffect(() => {
    trackerService.track({
      type: EVENT_NAME_FOR_USER_FLOW.MemberLoginFormShow,
      data: {
        event_category: EVENT_CATEGORY_FOR_USER_FLOW.MemberLogin,
        property: PROPERTY_FOR_USER_FLOW.third_party_login_option,
        value: getThirdPartyOptions(
          isFacebookLoginEnabled,
          isLineLoginEnabled,
          isGoogleSignUpLoginEnabled,
        ),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    refReCAPTCHA,
    refCheckboxReCAPTCHA,
    recaptchaCheckboxToken,
    isRecaptchaError,
    getRecaptchaToken,
    isShowCheckboxRecaptcha,
    setIsShowCheckboxRecaptcha,
    onRecaptchaCheckboxCallback,
  } = useRecaptcha({ callback: handleSubmit(onSubmit) });

  return (
    <Box>
      {hasMemberJoinNotice && !isOnePageStorePopup && (
        <MembershipPromoBannerImage
          image={image}
          currentLanguage={currentLanguage}
        />
      )}
      <Box mb="72px" px="24px">
        <TitleText mb="30px">{t('Sign In')}</TitleText>
        {isVariantLoading ? (
          <Box display="flex" justifyContent="center" py="12px">
            <img src="https://static.shoplineapp.com/web/v1/img/loaders/loader1.gif" />
          </Box>
        ) : (
          isSocialLoginEnabled &&
          hasThirdpartyLoginUIRevamp &&
          isVariantUI && (
            <>
              <Center>
                <HStack spacing="12px" width="100%">
                  {isFacebookLoginEnabled && (
                    <FacebookAccount
                      e2eId="login-facebook_icon"
                      currentPanel={PANEL.LOGIN}
                      isButtonStyle
                    />
                  )}
                  {isGoogleSignUpLoginEnabled && (
                    <GoogleAccountButton
                      e2eId="login-google_icon"
                      currentPanel={PANEL.LOGIN}
                      isButtonStyle
                    />
                  )}
                  {isLineLoginEnabled && (
                    <LineAccount
                      e2eId="login-line_icon"
                      currentPanel={PANEL.LOGIN}
                      isButtonStyle
                    />
                  )}
                </HStack>
              </Center>
              <SocialAccountDivider />
            </>
          )
        )}

        <ErrorBanner errorMessages={errorMessages} />
        {isOnePageStorePopup && showResetPasswordHint && (
          <Alert status="info" variant="subtle" mb="24px">
            <Box>{t('Login - Info - module reset password hint')}</Box>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb="10px">
            <Input
              data-e2e-id="login-email_input"
              label={t(i18nKey.label)}
              placeholder={t(i18nKey.placeholder)}
              {...getInputFieldProps({
                field: emailAndMobileField,
                error: errors?.emailAndMobile,
              })}
            />
          </Box>
          <Box mb="10px">
            <Input.PasswordInput
              data-e2e-id="login-password_input"
              label={t('Password')}
              placeholder={t('Password (8 characters minimum)')}
              {...getInputFieldProps({
                field: passwordField,
                error: errors?.password,
              })}
            />
          </Box>
          <Box my="20px">
            <Link
              data-e2e-id={SIGNUP_LOGIN_E2E_TEST_IDS.LOGIN_FORGOT_PASSWORD_LINK}
              href={RESET_PASSWORD_PATH}
              fontSize="1.2rem"
              color="#3493FB"
              {...(isOnePageStorePopup
                ? {
                    onClick: handleResetPasswordClick,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {})}
            >
              {t('Forgot Password')}
            </Link>
          </Box>
          <RecaptchaCheckbox
            refReCAPTCHA={refReCAPTCHA}
            refCheckboxReCAPTCHA={refCheckboxReCAPTCHA}
            isShowCheckboxRecaptcha={isShowCheckboxRecaptcha}
            onRecaptchaCheckboxCallback={onRecaptchaCheckboxCallback}
          />
          <Button
            data-e2e-id="login-submit_button"
            isFullWidth
            type="submit"
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
          >
            {t('Lets go')}
          </Button>
          <OAuthApplicationLinks
            applications={customerAuthenticationOauthApplications}
          />
          {isVariantLoading ? (
            <Box display="flex" justifyContent="center" py="12px">
              <img src="https://static.shoplineapp.com/web/v1/img/loaders/loader1.gif" />
            </Box>
          ) : (
            isSocialLoginEnabled &&
            (!hasThirdpartyLoginUIRevamp || !isVariantUI) && (
              <Box mt="24px">
                <SocialAccountDivider />
                <Center>
                  <HStack spacing="12px">
                    {isFacebookLoginEnabled && (
                      <FacebookAccount
                        e2eId="login-facebook_icon"
                        currentPanel={PANEL.LOGIN}
                      />
                    )}
                    {isGoogleSignUpLoginEnabled && (
                      <GoogleAccountButton
                        e2eId="login-google_icon"
                        currentPanel={PANEL.LOGIN}
                      />
                    )}
                    {isLineLoginEnabled && (
                      <LineAccount
                        e2eId="login-line_icon"
                        currentPanel={PANEL.LOGIN}
                      />
                    )}
                  </HStack>
                </Center>
              </Box>
            )
          )}
        </form>
      </Box>
      <Box px="24px">
        <TitleText mb="24px">{t('Not a member')}</TitleText>
        {hasMemberJoinNotice && (
          <MembershipPromoBannerText
            text={text}
            currentLanguage={currentLanguage}
            defaultTextConditions={defaultTextConditions}
            t={t}
          />
        )}
        <Button
          data-e2e-id={SIGNUP_LOGIN_E2E_TEST_IDS.LOGIN_SIGN_UP_BUTTON}
          isFullWidth
          onClick={() => setCurrentPanel(PANEL.SIGN_UP)}
          variant="outline"
        >
          {t('Sign Up')}
        </Button>
      </Box>
    </Box>
  );
}
